// KM-E9 / US-E9.2 · rendering the parameter CLASS table (spec luku 7.3) — shared by the platform-settings
// and tenant-settings pages.
//
// Everything on screen here is the server's answer. The grouping is by the `class` the API put each
// parameter in, the order of the groups is the spec's (least delegated first) with any class the server
// names but this file does not know rendered as its own group under its raw code, and a refusal always
// carries the server's own `reason` — because "no" without a reason is indistinguishable from "not
// implemented", and the whole point of the class table is that a refusal names the rule that produced it.
//
// The one distinction the copy must not blur (US-E9.2a): `settable: false` on a TENANT-FREE parameter is
// not a lock. Reminders, branding and report distribution are the tenant's by class and simply have no
// write surface in this software version — so they read "not configurable here yet", never "locked".

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  PARAMETER_CLASS_ORDER,
  type QuestionnaireParameterClassRow,
  type QuestionnaireParameterState,
} from "./questionnaireParametersApi";

/**
 * One parameter as this file renders it. The static class table and the per-questionnaire resolution are
 * the same row with two extra facts, so both pages feed the same shape: on the static table nothing is
 * settable "now" for a particular questionnaire, and `settable` is the only settability there is.
 */
export interface ParameterView {
  key: string;
  class: string;
  setBy: string;
  when: string;
  settable: boolean;
  endpoint: string | null;
  description: string;
  settableNow: boolean;
  reason: string | null;
  value: string | null;
}

/** A per-questionnaire resolution, as it arrives. */
export function fromState(row: QuestionnaireParameterState): ParameterView {
  return { ...row };
}

/** A row of the STATIC class table: no questionnaire, so "settable now" is just "settable". */
export function fromClassRow(row: QuestionnaireParameterClassRow): ParameterView {
  return { ...row, settableNow: row.settable, reason: null, value: null };
}

/**
 * What state marker a row carries. Derived ENTIRELY from the server's own fields — `settableNow` first,
 * then the class — so a reclassification server-side changes this screen without a frontend change.
 */
export function parameterState(p: ParameterView): string {
  if (p.settableNow) return "editable";
  switch (p.class) {
    case "derived":
      return "derived";
    case "locked":
      return "locked";
    case "platform":
      return "platform";
    case "tenant_locked_at_publish":
      return "lockedAtPublish";
    // Tenant-free and refused: either the class has no write surface yet (reminders, branding, report
    // distribution) or this questionnaire's lifecycle refuses it right now (a closed open window).
    default:
      return p.settable ? "notEditableNow" : "notYet";
  }
}

/** Group the parameters by class, spec order first, then any class the server named that we do not know. */
function groupByClass(parameters: ParameterView[]): [string, ParameterView[]][] {
  const groups = new Map<string, ParameterView[]>();
  for (const p of parameters) {
    const bucket = groups.get(p.class);
    if (bucket) bucket.push(p);
    else groups.set(p.class, [p]);
  }
  const known = PARAMETER_CLASS_ORDER.filter((c) => groups.has(c)).map(
    (c) => [c as string, groups.get(c)!] as [string, ParameterView[]],
  );
  const unknown = [...groups.entries()].filter(
    ([c]) => !(PARAMETER_CLASS_ORDER as readonly string[]).includes(c),
  );
  return [...known, ...unknown];
}

/** One parameter card: its name, its class, what the value is, and — when refused — the rule that refused it. */
function ParameterCard({
  parameter,
  control,
}: {
  parameter: ParameterView;
  control?: ReactNode;
}) {
  const { t } = useTranslation();
  const state = parameterState(parameter);
  const isDerived = parameter.class === "derived";
  return (
    <li
      className="parameter"
      data-testid="parameter"
      data-parameter={parameter.key}
      data-class={parameter.class}
      data-state={state}
      data-settable-now={String(parameter.settableNow)}
    >
      <div className="parameter__head">
        <h3 className="parameter__name">
          {t(`questionnaires.settings.parameter.${parameter.key}`, { defaultValue: parameter.key })}
        </h3>
        <code className="parameter__key">{parameter.key}</code>
        <span className="badge" data-state={state} data-testid="parameter-state">
          {t(`questionnaires.settings.state.${state}`, { defaultValue: state })}
        </span>
      </div>

      <dl className="parameter__meta">
        <div>
          <dt>{t("questionnaires.settings.setBy")}</dt>
          <dd data-testid="parameter-set-by">
            {t(`questionnaires.settings.actor.${parameter.setBy}`, { defaultValue: parameter.setBy })}
          </dd>
        </div>
        <div>
          <dt>{t("questionnaires.settings.when")}</dt>
          <dd data-testid="parameter-when">
            {t(`questionnaires.settings.window.${parameter.when}`, { defaultValue: parameter.when })}
          </dd>
        </div>
        <div>
          <dt>{t("questionnaires.settings.valueInForce")}</dt>
          <dd data-testid="parameter-value" data-value={parameter.value ?? ""}>
            {parameter.value ?? (
              <span className="questionnaire-report__muted">
                {t("questionnaires.settings.noStoredValue")}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {/* The server's own words about the parameter — the description is the API's, not this screen's. */}
      <p className="parameter__description" data-testid="parameter-description">
        {parameter.description}
      </p>

      {/* A DERIVED value is shown with the formula it falls out of and NO input control: it is what the
          instrument already says, not a judgement anyone makes. The formula defaults to the server's own
          description, so a parameter this build has never heard of still states where its value comes from. */}
      {isDerived && (
        <p className="parameter__formula" data-testid="parameter-formula">
          <span className="parameter__formula-label">{t("questionnaires.settings.formulaLabel")}</span>{" "}
          <code>
            {t(`questionnaires.settings.formula.${parameter.key}`, {
              defaultValue: parameter.description,
            })}
          </code>
        </p>
      )}

      {/* The refusal, in the server's words. Rendered whenever it sent one — including for a parameter
          that IS settable now but carries a bound (k is raise-only after publish; closesAt may not move
          into the past), because those are the rules the expert needs before typing, not after. */}
      {parameter.reason && (
        <p
          className={parameter.settableNow ? "parameter__rule" : "banner banner--info parameter__rule"}
          role={parameter.settableNow ? undefined : "status"}
          data-testid="parameter-reason"
        >
          {parameter.reason}
        </p>
      )}

      {control && (
        <div className="parameter__control" data-testid="parameter-control">
          {control}
        </div>
      )}
    </li>
  );
}

export interface ParameterSectionsProps {
  parameters: ParameterView[];
  /** The control for a parameter, when this page offers one. Nothing rendered when it returns null. */
  renderControl?: (parameter: ParameterView) => ReactNode;
  /** Optional filter — the platform page renders one class, the tenant page renders the rest. */
  filter?: (parameter: ParameterView) => boolean;
}

/** The class table as sections: one per class the server used, in spec order, each with its parameters. */
export function ParameterSections({ parameters, renderControl, filter }: ParameterSectionsProps) {
  const { t } = useTranslation();
  const shown = filter ? parameters.filter(filter) : parameters;
  const groups = groupByClass(shown);

  if (groups.length === 0) {
    return (
      <p className="empty-state" role="status" data-testid="parameters-empty">
        {t("questionnaires.settings.noParameters")}
      </p>
    );
  }

  return (
    <>
      {groups.map(([cls, rows]) => (
        <section
          key={cls}
          className="parameter-class"
          data-testid="parameter-class"
          data-class={cls}
          aria-labelledby={`parameter-class-${cls}`}
        >
          <h2 id={`parameter-class-${cls}`}>
            {t(`questionnaires.settings.class.${cls}`, { defaultValue: cls })}
          </h2>
          <p className="questionnaire-report__hint">
            {t(`questionnaires.settings.classHint.${cls}`, { defaultValue: "" })}
          </p>
          <ul className="parameter-list">
            {rows.map((p) => (
              <ParameterCard key={p.key} parameter={p} control={renderControl?.(p)} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
